import {useQuery} from '@tanstack/react-query';
import {useConfig} from '@lore/config';

/**
 * @typedef {Object} SiteLeadItem
 * @property {number} id
 * @property {string} name
 * @property {boolean} isActive
 * @property {string} search_text
 * @property {number} recent_users
 * @property {string} recent_activity
 */

/**
 * @typedef {Object} SiteLeadsResponse
 * @property {SiteLeadItem[]} data
 * @property {number} page
 * @property {number} pageSize
 * @property {number} total
 */

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {SiteLeadItem}
 */
export const _SiteLeadItem = {};

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {SiteLeadsResponse}
 */
export const _SiteLeadsResponse = {};

const STALE_TIME = 15 * 60 * 1000; // 15 minutes

/**
 * Custom hook to fetch site leads from the core API.
 *
 * @param {Object} params - Query parameters for the request.
 * @param {number} [params.page=1] - The page number to fetch.
 * @param {number} [params.pageSize=20] - The number of items per page.
 * @param {string|null} [params.q=null] - Search query string.
 * @param {Object} options - Configuration options like 'enabled' to control the query.
 * @returns {import('@tanstack/react-query').UseQueryResult<SiteLeadsResponse>} React Query result object.
 */
export default function useCoreSiteLeads(
  {page = 1, pageSize = 20, q = null} = {},
  {enabled = true} = {}
) {
  const config = useConfig();
  const {apiRoot, headers} = config.connections.default;

  const query = useQuery({
    queryKey: ['sites', 'leads', page, pageSize, q],

    queryFn: () => {
      const url = new URL(`${apiRoot}/core/sites/leads`);

      // Add query parameters
      if (page != null) url.searchParams.append('page', String(page));
      if (pageSize != null) url.searchParams.append('pageSize', String(pageSize));
      if (q != null) url.searchParams.append('q', q);

      return fetch(url.toString(), {
        method: 'GET',
        headers: headers(),
      }).then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch site leads`);
        }
        return res.json();
      });
    },

    staleTime: STALE_TIME,
    enabled: enabled,
  });

  return {
    ...query,
  };
}
